/**
 * PDF generation and handling module for the inventor form
 */
class InventorFormPDF {
    constructor() {
        this.pdfTemplateUrl = 'https://cdn.shopify.com/s/files/1/0790/4087/2482/files/MNDA_TEMPLATE.pdf';
    }

    /**
     * Generates and uploads a filled MNDA PDF
     * @param {Object} formData - The form data to fill in the PDF
     * @returns {Promise<string>} The URL of the uploaded PDF
     */
    async generateAndUploadPDF(formData) {
        try {
            const pdfDoc = await this.loadAndFillPDF(formData);
            const pdfBytes = await pdfDoc.save();
            return await this.uploadPDF(pdfBytes);
        } catch (error) {
            console.error('Error generating or uploading PDF:', error);
            throw error;
        }
    }

    /**
     * Loads the PDF template and fills it with form data
     * @param {Object} formData - The form data to fill in the PDF
     * @returns {Promise<PDFDocument>} The filled PDF document
     */
    async loadAndFillPDF(formData) {
        const response = await fetch(this.pdfTemplateUrl);
        const arrayBuffer = await response.arrayBuffer();
        const pdfDoc = await PDFLib.PDFDocument.load(arrayBuffer);
        const font = await pdfDoc.embedFont(PDFLib.StandardFonts.Helvetica);
        
        const firstPage = pdfDoc.getPages()[0];
        const lastPage = pdfDoc.getPages()[pdfDoc.getPageCount() - 1];

        // Draw inventor trade name
        this.drawText(firstPage, formData.inventorTradeName, { x: 72, y: 355, size: 9, font });

        // Draw address
        this.drawText(firstPage, formData.streetAddress, { x: 130, y: 315, size: 8, font });
        this.drawText(firstPage, formData.city, { x: 130, y: 305, size: 8, font });
        this.drawText(firstPage, `${formData.stateProvince}, ${formData.countryRegion}, ${formData.postalCode}`, { x: 130, y: 293, size: 8, font });

        // Draw contact information
        this.drawText(firstPage, formData.coordinator, { x: 140, y: 270, size: 8, font });
        this.drawText(firstPage, formData.email, { x: 140, y: 257, size: 8, font });
        this.drawText(firstPage, formData.phone, { x: 110, y: 245, size: 8, font });

        // Draw submission name (Purpose field)
        this.drawText(firstPage, formData.submissionName, { x: 72, y: 82, size: 8, font });

        // Draw signature information
        this.drawText(lastPage, formData.mndaSignedBy, { x: 415, y: 510, size: 9, font });
        this.drawText(lastPage, `${formData.firstName} ${formData.lastName}`, { x: 360, y: 475, size: 9, font });
        this.drawText(lastPage, formData.title, { x: 360, y: 438, size: 9, font });
        this.drawText(lastPage, new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), { x: 360, y: 400, size: 9, font });

        return pdfDoc;
    }

    /**
     * Draws text on a PDF page with the given options
     * @param {PDFPage} page - The PDF page to draw on
     * @param {string} text - The text to draw
     * @param {Object} options - Drawing options (x, y, size, font)
     */
    drawText(page, text, { x, y, size, font }) {
        page.drawText(text, {
            x,
            y,
            size,
            font,
            color: PDFLib.rgb(0, 0, 0),
        });
    }

    /**
     * Uploads the PDF to the server
     * @param {Uint8Array} pdfBytes - The PDF file bytes
     * @returns {Promise<string>} The URL of the uploaded PDF
     */
    async uploadPDF(pdfBytes) {
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const file = new File([blob], "MNDA.pdf", { type: "application/pdf" });
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(gadgetAppUrl + '/azureUpload', {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`File upload failed: ${response.status}`);
        }

        const data = await response.json();
        return data.fileName;
    }
}

// Export for use in other modules
window.InventorFormPDF = InventorFormPDF;
